/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useRef, useState } from 'react';

import { disableScrolling } from 'bundles/browse/utils';
import SimplifiedExploreButton from 'bundles/megamenu/components/SimplifiedExploreButton';
import SimplifiedMegaMenuContainer from 'bundles/megamenu/components/SimplifiedMegaMenuContainer';
import { useHasMetaNav } from 'bundles/page-header/contexts/PageHeaderContext';

import _t from 'i18n!nls/megamenu';

const SimplifiedMegaMenuWrapper = () => {
  const pageHeaderHeight = () => {
    return useHasMetaNav() ? '105px' : '65px';
  };

  const styles = {
    simplifiedMegaMenuOverlay: css`
      background-color: inherit;
      position: fixed;
      top: ${pageHeaderHeight()};
      left: 0;
      z-index: 3000;
      width: 100vw;
      height: calc(100% - ${pageHeaderHeight()});
    `,
  };

  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  const divRef = useRef<HTMLDivElement | null>(null);
  const cursorIsOnMenuOrExploreButton = useRef<boolean | undefined>(undefined);

  // close megamenu when component and button lose focus or esc key is pressed
  const handleFocusOut = (event: FocusEvent) => {
    if (divRef.current && !divRef.current.contains(event.relatedTarget as Node)) {
      closeMenu();
    }
  };
  const handleKeyUp = (event: KeyboardEvent) => {
    if (divRef.current && event.key === 'Escape') {
      closeMenu();
    }
  };

  const attachEventListeners = () => {
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keyup', handleKeyUp);
  };

  const detatchEventListeners = () => {
    document.removeEventListener('focusout', handleFocusOut);
    document.removeEventListener('keyup', handleKeyUp);
  };

  const setMenuOverlay = () => {
    if (cursorIsOnMenuOrExploreButton.current) {
      attachEventListeners();
      disableScrolling(true);
      setMenuIsOpen(true);
    }
  };

  const openMenu = () => {
    cursorIsOnMenuOrExploreButton.current = true;
    if (!menuIsOpen) {
      setTimeout(() => {
        setMenuOverlay();
      }, 100);
    }
  };

  const openMenuUsingKeyboard = () => {
    cursorIsOnMenuOrExploreButton.current = true;
    if (!menuIsOpen) {
      setMenuOverlay();
    }
  };

  const closeMenu = () => {
    cursorIsOnMenuOrExploreButton.current = false;
    setTimeout(() => {
      if (!cursorIsOnMenuOrExploreButton.current) {
        detatchEventListeners();
        disableScrolling(false);
        setMenuIsOpen(false);
      }
    }, 100);
  };

  return (
    <div data-testid="simplified-megaMenu-wrapper" ref={divRef}>
      <SimplifiedExploreButton
        menuIsOpen={menuIsOpen}
        openMenu={openMenu}
        openMenuUsingKeyboard={openMenuUsingKeyboard}
        closeMenu={closeMenu}
      />
      {menuIsOpen && (
        <div css={styles.simplifiedMegaMenuOverlay}>
          <nav aria-label={_t('Main Menu')}>
            <SimplifiedMegaMenuContainer menuIsOpen={menuIsOpen} openMenu={openMenu} closeMenu={closeMenu} />
          </nav>
        </div>
      )}
    </div>
  );
};

export default SimplifiedMegaMenuWrapper;
